import { ForwardedRef, ReactNode, useEffect, useState } from 'react';
import { Banner, SelectionCardGroup, Stack, Tab, Tabs, Typography } from '@optimus-web/core';
import { PaymentRequestType, SimpleAmount } from '@mews-commander/data-access';
import { lt, useFeatureFlag } from '@mews/framework';
import { DefaultErrorBoundary, LocalizedTypography } from '@mews-commander/ui';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { PaymentFormProps, PaymentFormRef } from './types';
import { DefaultFormProvider, DefaultSelectedFormValues, ReadOnlyFormFields } from './useDefaultFormValues';
import { useGetChargeGuestOptionsSuspense } from './hooks/useGetChargeGuestOptionsSuspense';
import { useGetRecordPaymentOptionsSuspense } from './hooks/useGetRecordPaymentOptionsSuspense';
import { ProcessPaymentType } from './ProcessPaymentType';
import { useGetDefaultPaymentMethodTypeSuspense } from './hooks/useGetDefaultPaymentMethodTypeSuspense';
import { ProcessPaymentForm } from './ProcessPaymentForm';
import { ProcessPaymentDialogPaymentMethodCardsOptions } from './useProcessPaymentDialog';

enum ProcessPaymentDialogTab {
    ChargeGuest = 'ChargeGuest',
    RecordPayment = 'RecordPayment',
}

interface ProcessPaymentDialogContentProps {
    currentFormRef: ForwardedRef<PaymentFormRef>;
    accountId: string;
    ownerIds?: string[];
    billId?: string;
    allowedPaymentTypes: ProcessPaymentType[];
    onClose?: () => void;
    onFormSuccess?: () => void;
    onFormSubmitting: () => void;
    onFormSubmitted: () => void;
    onChangePaymentType?: (paymentType: ProcessPaymentType) => void;
    canLinkInvoices?: boolean;
    defaultSelectedFormValues?: DefaultSelectedFormValues;
    readOnlyFormFields?: ReadOnlyFormFields;
    paymentMethodCardsOptions?: ProcessPaymentDialogPaymentMethodCardsOptions;
    hideSinglePaymentMethodTitle?: boolean;
    isLinkingInvoiceDropdownVisible?: boolean;
    allowedPaymentRequestTypeOptions?: PaymentRequestType[];
    billAmount?: SimpleAmount;
}

const SinglePaymentMethodTitle = ({ label }: { label: ReactNode }) => (
    <Typography textStyle="titleSmallStrong" as="div">
        {label}
    </Typography>
);

const PaymentMethodCardsTextDescription = ({ label, description }: { label: ReactNode; description: ReactNode }) => (
    <>
        <Typography textStyle="titleSmallStrong" as="div">
            {label}
        </Typography>
        <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
            {description}
        </Typography>
    </>
);

export const ProcessPaymentDialogContent = ({
    currentFormRef,
    accountId,
    billId,
    allowedPaymentTypes,
    onClose,
    onFormSuccess,
    onFormSubmitting,
    onFormSubmitted,
    onChangePaymentType,
    canLinkInvoices,
    isLinkingInvoiceDropdownVisible = true,
    defaultSelectedFormValues,
    readOnlyFormFields,
    paymentMethodCardsOptions,
    hideSinglePaymentMethodTitle,
    ownerIds,
    allowedPaymentRequestTypeOptions,
    billAmount,
}: ProcessPaymentDialogContentProps) => {
    const chargeGuestOptions = useGetChargeGuestOptionsSuspense({
        allowedPaymentTypes,
        allowedPaymentRequestTypeOptions,
    });
    const recordPaymentOptions = useGetRecordPaymentOptionsSuspense({
        allowedPaymentTypes,
        allowedPaymentRequestTypeOptions,
    });
    const defaultPaymentMethodType = useGetDefaultPaymentMethodTypeSuspense({ allowedPaymentTypes });
    const isPciProxyMaintenanceDisabledPaymentsEnabled = useFeatureFlag(
        FeatureFlag.pciProxyMaintenanceDisabledPaymentsEnabled
    );
    const [activeTab, setActiveTab] = useState(
        chargeGuestOptions.length > 0 ? ProcessPaymentDialogTab.ChargeGuest : ProcessPaymentDialogTab.RecordPayment
    );
    const [paymentType, setPaymentType] = useState(defaultPaymentMethodType);

    const paymentMethodCardsForActiveTab =
        activeTab === ProcessPaymentDialogTab.ChargeGuest ? chargeGuestOptions : recordPaymentOptions;

    const availablePaymentMethodCards = paymentMethodCardsOptions?.onlyCardsForMultipleMethods
        ? [...chargeGuestOptions, ...recordPaymentOptions].sort(
              (a, b) => allowedPaymentTypes.indexOf(a.value) - allowedPaymentTypes.indexOf(b.value) // sorts items based on their order in allowedPaymentTypes
          )
        : paymentMethodCardsForActiveTab;

    const handleTabChange = (_e: unknown, value: string) => {
        setPaymentType(
            value === ProcessPaymentDialogTab.ChargeGuest ? chargeGuestOptions[0]?.value : recordPaymentOptions[0].value
        );
        setActiveTab(value as ProcessPaymentDialogTab);
    };

    const handleSuccess = () => {
        onFormSuccess?.();
        onClose?.();
    };

    const formProps = {
        ref: currentFormRef,
        accountId,
        billId,
        onSuccess: handleSuccess,
        onSubmitting: onFormSubmitting,
        onSubmitted: onFormSubmitted,
    } satisfies PaymentFormProps;

    useEffect(() => {
        onChangePaymentType?.(paymentType);
    }, [paymentType, onChangePaymentType]);

    return (
        <Stack vertical spacing="300">
            {!paymentMethodCardsOptions?.onlyCardsForMultipleMethods &&
                chargeGuestOptions.length > 0 &&
                recordPaymentOptions.length > 0 && (
                    <Tabs activeTabValue={activeTab} onTabClick={handleTabChange}>
                        <Tab
                            label={lt('ProcessPaymentMewsPaymentsTabTitle')}
                            id={ProcessPaymentDialogTab.ChargeGuest}
                            value={ProcessPaymentDialogTab.ChargeGuest}
                        />
                        <Tab
                            label={lt('ProcessPaymentExternalPaymentsTabTitle')}
                            id={ProcessPaymentDialogTab.RecordPayment}
                            value={ProcessPaymentDialogTab.RecordPayment}
                        />
                    </Tabs>
                )}
            {isPciProxyMaintenanceDisabledPaymentsEnabled && (
                <Banner
                    primaryText={
                        <LocalizedTypography
                            textStyle="bodyMedium"
                            textKey={TextKey.ProcessPaymentPciProxyMaintenanceBannerText}
                            as="div"
                        />
                    }
                    variant="info"
                />
            )}

            {availablePaymentMethodCards.length > 1 && (
                <SelectionCardGroup
                    cardVariant="vertical"
                    type="radio"
                    inputValue={paymentType}
                    onChange={(_e, value) => setPaymentType(value as ProcessPaymentType)}
                    options={availablePaymentMethodCards}
                />
            )}
            {availablePaymentMethodCards.length === 1 &&
                (paymentMethodCardsOptions?.onlyDescriptionForSingleMethod ? (
                    <PaymentMethodCardsTextDescription
                        label={availablePaymentMethodCards[0].label}
                        description={availablePaymentMethodCards[0].description}
                    />
                ) : (
                    !hideSinglePaymentMethodTitle && (
                        <SinglePaymentMethodTitle label={availablePaymentMethodCards[0].label} />
                    )
                ))}
            <DefaultErrorBoundary>
                <DefaultFormProvider
                    accountId={accountId}
                    billId={billId}
                    canLinkInvoices={canLinkInvoices}
                    isLinkingInvoiceDropdownVisible={isLinkingInvoiceDropdownVisible}
                    defaultSelectedFormValues={defaultSelectedFormValues}
                    readOnlyFormFields={readOnlyFormFields}
                    ownerIds={ownerIds}
                    allowedPaymentRequestTypeOptions={allowedPaymentRequestTypeOptions}
                    billAmount={billAmount}
                >
                    <ProcessPaymentForm paymentType={paymentType} {...formProps} />
                </DefaultFormProvider>
            </DefaultErrorBoundary>
        </Stack>
    );
};
